import gql from 'graphql-tag';
import * as Sentry from '@sentry/browser';
import momentTimezone from 'moment-timezone';
import clevertap from 'clevertap-web-sdk';

import auth from '../services/Auth';
import http from '../services/Http';
import { analyticsClient } from '../services/analytics';

const PROVIDER_ROLE = 'COACH';
export const COACH_PROVIDER_TYPE = 'Coach';
export const THERAPIST_PROVIDER_TYPE = 'Therapist';
export const PSYCHIATRIST_PROVIDER_TYPE = 'Psychiatrist';

export class CurrentUser {
  constructor(user = {}) {
    this.avatar = user.avatar;
    this.email = user.email;
    this.newEmailAddressNotConfirmed = user.newEmailAddressNotConfirmed;
    this.firstName = user.firstName;
    this.lastName = user.lastName;
    this.role = user.role;
    this.providerInvoicingOnly = user.providerInvoicingOnly;
    this.uuid = user.uuid;
    this.id = user.id;
    this.birthDate = user.birthDate;
    this.languagePreferences = user.languagePreferences;
    this.country = user.country;
    this.zipCode = user.zipCode;
    this.city = user.city;
    this.state = user.state;
    this.introduction = user.introduction;
    // TODO: Check to see if specialization is still used
    this.specialization = user.specialization;
    this.credentials = user.credentials;
    this.goalInterests = user.goalInterests;
    this.providerType = user.providerType;
    this.localTimezone = user.localTimezone;
    this.newClientCapacity = user.newClientCapacity;
    this.providerSchedulingType = user.providerSchedulingType;
  }

  get isProvider() {
    return this.role === PROVIDER_ROLE;
  }

  get isCoach() {
    return this.providerType === COACH_PROVIDER_TYPE;
  }

  get isTherapist() {
    return this.providerType === THERAPIST_PROVIDER_TYPE;
  }

  get isPsychiatrist() {
    return this.providerType === PSYCHIATRIST_PROVIDER_TYPE;
  }
}

const INITIAL_STATE = {
  isAuthenticated: false,
  currentUser: {},
};

export const user = {
  state: {
    ...INITIAL_STATE,
  },
  reducers: {
    setIsAuthenticated(state, isAuthenticated) {
      return { ...state, isAuthenticated };
    },
    setCurrentUserAndAuthenticated(state, { currentUser, isAuthenticated }) {
      return { ...state, currentUser, isAuthenticated };
    },
    updateCurrentUser(state, props) {
      return { ...state, currentUser: { ...state.currentUser, ...props } };
    },
    reset() {
      return INITIAL_STATE;
    },
  },
  effects: (dispatch) => ({
    async fetchCurrentUser(payload, rootState) {
      const response = await rootState.network.client.query({
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        query: GET_CURRENT_USER,
        fetchPolicy: 'network-only',
      });

      const currentUser = new CurrentUser(response.data.me);

      if (!currentUser.localTimezone) {
        const timezoneGuess = momentTimezone.tz.guess();
        const timezoneGuessValid = typeof timezoneGuess == 'string' && timezoneGuess !== '';

        if (timezoneGuessValid) {
          // eslint-disable-next-line no-console
          dispatch.user.updateUserTimezone({ timezone: timezoneGuess }).catch(console.error);
        }
      }

      analyticsClient.identify(user.uuid, {
        providerType: currentUser.providerType,
      });

      clevertap.onUserLogin.push({
        Site: {
          Identity: user.uuid,
          Email: user.email,
          'MSG-push': true,
        },
      });

      const clevertapId = clevertap.getCleverTapID();
      if (clevertapId) {
        http.formDataPATCH(`accounts/${currentUser.id}/`, {
          clevertapId,
        });
      }

      Sentry.configureScope((scope) =>
        scope.setUser({
          id: currentUser.uuid,
        }),
      );

      dispatch.user.setCurrentUserAndAuthenticated({ currentUser, isAuthenticated: true });
    },
    async logout({ nextUrl }, rootState) {
      await auth.logout();
      dispatch.chat.reset();
      dispatch.user.reset();

      if (rootState.router.history.push) {
        rootState.router.history.push(nextUrl);
      } else {
        // history might not be a react-router object yet, and so might not have the `push` method.
        window.location = nextUrl;
      }
    },
    updateUserTimezone(payload, rootState) {
      dispatch.user.updateCurrentUser({ localTimezone: payload.timezone });

      return http.formDataPATCH(`accounts/${rootState.user.currentUser.id}/`, {
        localTimezone: payload.timezone,
      });
    },
  }),
  selectors: (_, createSelector) => ({
    selectedGoalCategoryIds() {
      return createSelector(
        (state) => state.user.currentUser.goalInterests || [],
        (goalInterests) => goalInterests.map((gi) => gi.goalCategory.id),
      );
    },
    latestEmail() {
      return createSelector(
        (state) => state.user.currentUser.newEmailAddressNotConfirmed,
        (state) => state.user.currentUser.email,
        (newEmailAddressNotConfirmed, email) => {
          // unconfirmed email takes precident. If no unconfirmed email, then return the `email`.
          return newEmailAddressNotConfirmed || email;
        },
      );
    },
  }),
};

export const getUserUuid = (state) => state.user.currentUser.uuid;
export const getUserProviderType = (state) => state.user.currentUser.providerType;

const GET_CURRENT_USER = gql`
  query getCurrentUser {
    me {
      email
      firstName
      lastName
      role
      avatar
      uuid
      id
      birthDate
      languagePreferences
      country
      zipCode
      city
      state
      providerInvoicingOnly
      introduction
      credentials
      newEmailAddressNotConfirmed
      providerType
      maximumMemberSlots
      newClientCapacity
      localTimezone
      providerSchedulingType
      goalInterests {
        id
        goalCategory {
          id
          name
          image
          isInOnboarding
          order
          matchWeight
        }
      }
    }
  }
`;
